import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { HOME } from "@/constants/testIds";
import { Send, Facebook, Youtube, MessageCircle } from "lucide-react";

const LOGO_URL = "/logo-192.png";

export const SOCIALS = {
  telegram: "https://t.me/MorningSundayTeer",
  whatsappChannel: "https://whatsapp.com/channel/0029Vb8MzS9F1YlWpvY4M81x",
  facebook: "https://www.facebook.com/share/18nZVtunj6/",
  youtube: "https://youtube.com/@morningteerliveresult?si=EVVZwViXcUBP7RGt",
};

export function TopBanner({ text }) {
  return (
    <div
      data-testid={HOME.banner}
      className="w-full bg-rose-600 text-white text-[11px] sm:text-xs font-bold tracking-[0.18em] uppercase text-center py-1.5"
    >
      {text || "DOWNLOAD ANDROID APP — OTP NOT REQUIRED"}
    </div>
  );
}

export function SiteHeader({ title }) {
  return (
    <header
      data-testid={HOME.header}
      className="sticky top-0 z-40 bg-sky-200 border-b border-sky-300 shadow-sm"
    >
      <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-3">
        <Link to="/" data-testid={HOME.logo} className="flex items-center gap-3 group">
          <img
            src={LOGO_URL}
            alt="Official Morning Teer — Fast, Accurate, Trusted"
            className="h-11 w-11 rounded-full shadow-md ring-2 ring-white group-hover:ring-emerald-500 transition"
          />
          <span className="font-heading font-black text-slate-900 text-xl sm:text-2xl tracking-tight">
            {title || "Official Morning Teer"}
          </span>
        </Link>
      </div>
    </header>
  );
}

const SOCIAL_ITEMS = [
  {
    key: "telegram",
    href: SOCIALS.telegram,
    label: "Telegram",
    tid: "footer-telegram",
    Icon: Send,
    bg: "bg-gradient-to-br from-sky-400 to-blue-600",
  },
  {
    key: "whatsapp",
    href: SOCIALS.whatsappChannel,
    label: "WhatsApp",
    tid: "footer-whatsapp",
    Icon: MessageCircle,
    bg: "bg-gradient-to-br from-emerald-400 to-green-600",
  },
  {
    key: "facebook",
    href: SOCIALS.facebook,
    label: "Facebook",
    tid: "footer-facebook",
    Icon: Facebook,
    bg: "bg-gradient-to-br from-blue-500 to-indigo-700",
  },
  {
    key: "youtube",
    href: SOCIALS.youtube,
    label: "YouTube",
    tid: "footer-youtube",
    Icon: Youtube,
    bg: "bg-gradient-to-br from-red-500 to-rose-700",
  },
];

export function SiteFooter() {
  const nav = [
    { to: "/about-us", label: "About Us" },
    { to: "/blog", label: "Blog" },
    { to: "/contact-us", label: "Contact" },
    { to: "/privacy-policy", label: "Privacy Policy" },
    { to: "/disclaimer", label: "Disclaimer" },
    { to: "/terms", label: "Terms & Conditions" },
  ];
  return (
    <footer className="mt-auto bg-[#0a1428] text-white border-t border-white/10">
      <div className="max-w-xl mx-auto px-5 py-8">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-5">
          <img
            src={LOGO_URL}
            alt="Official Morning Teer logo"
            className="h-12 w-12 rounded-full ring-2 ring-emerald-500/40 shadow-lg"
          />
          <div>
            <div className="font-heading font-black text-lg">Official Morning Teer</div>
            <div className="text-[10px] font-semibold tracking-[0.2em] text-sky-400 uppercase">
              OfficialMorningTeer.com
            </div>
          </div>
        </div>

        {/* Follow us / Social */}
        <div className="mb-6">
          <div className="text-center mb-3">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-400/10 ring-1 ring-amber-400/40">
              <span className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-300">
                ★ Follow Us ★
              </span>
            </div>
            <div className="text-xs text-slate-300 mt-2">
              Join our channels for instant result updates
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {SOCIAL_ITEMS.map((s) => (
              <a
                key={s.key}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={s.tid}
                className={`group relative flex items-center gap-3 rounded-2xl ${s.bg} px-3.5 py-3.5 shadow-lg shadow-black/30 hover:scale-[1.03] active:scale-95 transition-transform`}
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/25 backdrop-blur ring-1 ring-white/30 text-white">
                  <s.Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div className="min-w-0">
                  <div className="text-[9px] font-bold uppercase tracking-widest text-white/80">
                    Join
                  </div>
                  <div className="text-sm font-heading font-black text-white truncate">
                    {s.label}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Nav links */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-6">
          {nav.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-sm text-slate-300 hover:text-white transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="pt-5 border-t border-white/10 text-center">
          <div className="text-xs text-slate-400">© 2026 OfficialMorningTeer.com</div>
          <div className="text-xs text-slate-500 mt-1">All Rights Reserved.</div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  // Brand name is fixed. We ignore any legacy site_title stored in the DB so
  // the header always shows the current brand, no matter what the production
  // settings collection contains.
  const BRAND = "Official Morning Teer";
  const [settings, setSettings] = useState({ banner_text: "" });

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setSettings({ ...r.data, site_title: BRAND }))
      .catch(() => {});
  }, []);

  return (
    <div className="App min-h-screen flex flex-col bg-slate-50">
      <SiteHeader title={BRAND} />
      <main className="flex-1 w-full">{children}</main>
      <SiteFooter />
    </div>
  );
}
